import { useRef, useEffect, useState, useCallback } from "react";

/**
 * useAudioProctoring (formerly useAudioProctor)
 * 
 * Real-time audio monitoring hook for exam proctoring.
 * 
 * Features:
 * - Microphone capture with noise cancellation (echo cancellation, noise suppression, auto gain)
 * - Voice activity detection using frequency analysis
 * - Multiple speaker estimation based on pitch/frequency clustering
 * - Background noise level monitoring with adaptive baseline
 * - Noise spike detection for potential signalling attempts
 * - Prolonged silence detection
 * - Real-time audio metrics and violation flags
 * - Configurable RMS threshold with onFlag callbacks
 * 
 * Usage:
 * ```tsx
 * const { startListening, stopListening, isListening, currentRms } = useAudioProctoring({
 *   rmsThreshold: 0.15,
 *   onFlag: (payload) => {
 *     console.log('Audio violation:', payload);
 *     // payload: { type: "background_noise", rms: number, timestamp: number }
 *   }
 * });
 * 
 * useEffect(() => {
 *   startListening(); // Start audio monitoring
 *   return () => stopListening(); // Cleanup
 * }, []);
 * ```
 * 
 * Violation Flags (via onFlag callback):
 * - "background_noise" - RMS exceeds threshold (default 0.15)
 * - "multiple_speakers" - More than one person speaking (sustained for >3s)
 * - "background_voice" - Low volume speech indicating background conversation
 * - "prolonged_silence" - No speech detected for >5 minutes
 * - "noise_spike" - Sudden loud noise 25dB above baseline
 * 
 * Performance:
 * - Analysis runs at 5 Hz (every 200ms)
 * - FFT size: 2048 samples
 * - Uses Web Audio API with hardware-accelerated processing
 * - Minimal CPU impact (<2% on modern devices)
 */

// ── Configuration constants ─────────────────────────────────────────────────
/** Audio analysis sample rate (lower = less CPU intensive) */
const FFT_SIZE = 2048;
/** Smoothing factor for frequency analysis */
const SMOOTHING_TIME_CONSTANT = 0.8;
/** Volume threshold to consider as speech (0-100) */
const SPEECH_VOLUME_THRESHOLD = 15;
/** Background noise baseline calculation window (ms) */
const NOISE_BASELINE_WINDOW = 5000;
/** Noise spike threshold (dB above baseline) */
const NOISE_SPIKE_THRESHOLD = 25;
/** Silence threshold - no speech for this duration triggers flag (ms) */
const PROLONGED_SILENCE_THRESHOLD = 300000; // 5 minutes
/** Ambient calibration duration (ms) */
const CALIBRATION_DURATION_MS = 5000;
/** Moving average window for RMS smoothing */
const MOVING_AVERAGE_WINDOW = 15;
/** Ignore impulse sounds shorter than this (ms) */
const SHORT_SOUND_IGNORE_MS = 1000;
/** Only flag sustained sound longer than this (ms) */
const SUSTAINED_SOUND_THRESHOLD_MS = 3000;
/** Cooldown between repeated warnings (ms) */
const WARNING_COOLDOWN_MS = 10000;
/** Voice frequency range (Hz) for human speech */
const VOICE_FREQ_MIN = 120;
const VOICE_FREQ_MAX = 3400;
/** Low-frequency hum below this range should be ignored for speech detection */
const HUM_FREQ_MAX = 120;
/** Update interval for audio analysis (ms) */
const ANALYSIS_INTERVAL = 200;
/** Debounce interval - don't fire same flag more than once per interval (ms) */
const FLAG_DEBOUNCE_INTERVAL = 3000; // 3 seconds

// ── Types ───────────────────────────────────────────────────────────────────
export interface AudioProctorConfig {
  /** RMS threshold for background_noise detection (0-1, default: 0.15) */
  rmsThreshold?: number;
  /** Callback fired when audio violations are detected */
  onFlag?: (payload: AudioFlagPayload) => void;
}

export type AudioFlagType =
  | "background_noise"
  | "background_speech"
  | "multiple_speakers"
  | "background_voice"
  | "prolonged_silence"
  | "noise_spike";

export type AudioWarningSeverity = "low" | "medium" | "high";

export interface AudioFlagPayload {
  type: AudioFlagType;
  rms: number;
  timestamp: number;
  severity?: AudioWarningSeverity;
  message?: string;
}

export interface AudioStatus {
  /** Current audio volume level (0-100) */
  currentVolume: number;
  /** Smoothed RMS value (0-1) */
  smoothedRms: number;
  /** Baseline ambient noise RMS (0-1) */
  baselineRms: number;
  /** Calibration progress (0-1) */
  calibrationProgress: number;
  /** Whether ambient calibration has completed */
  isCalibrated: boolean;
  /** Whether voice/speech is currently detected */
  voiceDetected: boolean;
  /** Estimated number of speakers (1-5+) */
  speakerCountEstimate: number;
  /** Active audio proctoring flags */
  flags: string[];
  /** Current warning severity level */
  warningLevel: AudioWarningSeverity | "none";
  /** Current warning message */
  warningMessage: string | null;
  /** Time since last speech detected (ms) */
  timeSinceLastSpeech: number;
  /** Whether microphone is active and listening */
  isListening: boolean;
  /** Permission status */
  permissionGranted: boolean;
}

interface AudioAnalysis {
  volume: number;
  voiceDetected: boolean;
  frequencyPeaks: number[];
  noiseLevel: number;
  voiceEnergy: number;
  totalEnergy: number;
  lowFrequencyEnergy: number;
}

/**
 * Analyze audio frame for voice, volume, and frequency characteristics
 */
function analyzeAudioFrame(
  analyser: AnalyserNode,
  dataArray: Uint8Array,
  frequencyArray: Uint8Array,
  sampleRate: number
): AudioAnalysis {
  analyser.getByteTimeDomainData(dataArray as any);
  analyser.getByteFrequencyData(frequencyArray as any);

  // Calculate RMS volume
  let sum = 0;
  for (let i = 0; i < dataArray.length; i++) {
    const normalized = (dataArray[i] - 128) / 128;
    sum += normalized * normalized;
  }
  const rms = Math.sqrt(sum / dataArray.length);
  const volume = Math.min(100, rms * 100);

  const binWidth = sampleRate / FFT_SIZE;
  const minBin = Math.floor(VOICE_FREQ_MIN / binWidth);
  const maxBin = Math.ceil(VOICE_FREQ_MAX / binWidth);
  const humBin = Math.floor(HUM_FREQ_MAX / binWidth);

  let voiceEnergy = 0;
  let totalEnergy = 0;
  let lowFrequencyEnergy = 0;
  const frequencyPeaks: number[] = [];

  for (let i = 0; i < frequencyArray.length; i++) {
    const energy = frequencyArray[i];
    totalEnergy += energy;

    if (i <= humBin) {
      lowFrequencyEnergy += energy;
    }

    if (i >= minBin && i <= maxBin) {
      voiceEnergy += energy;
      if (
        energy > 120 &&
        energy > (frequencyArray[i - 1] || 0) &&
        energy > (frequencyArray[i + 1] || 0)
      ) {
        frequencyPeaks.push(i * binWidth);
      }
    }
  }

  const voiceRatio = totalEnergy > 0 ? voiceEnergy / totalEnergy : 0;
  const voiceDetected = voiceRatio > 0.28 && volume > SPEECH_VOLUME_THRESHOLD;

  const noiseLevel = Math.min(100, (totalEnergy - voiceEnergy) / 2550);

  return {
    volume,
    voiceDetected,
    frequencyPeaks,
    noiseLevel,
    voiceEnergy,
    totalEnergy,
    lowFrequencyEnergy,
  };
}

/**
 * Estimate speaker count based on frequency peak distribution
 * Multiple distinct pitch peaks suggest multiple speakers
 */
function estimateSpeakerCount(
  recentPeaks: number[][],
  currentPeaks: number[]
): number {
  if (currentPeaks.length === 0) return 0;

  // Cluster peaks across recent frames
  const allPeaks = recentPeaks.reduce<number[]>(
    (acc, peaks) => acc.concat(peaks),
    []
  ).concat(currentPeaks);
  if (allPeaks.length < 3) return 1;

  // Simple clustering: group peaks within ~50Hz of each other
  const clusters: number[][] = [];
  const tolerance = 50;

  for (const peak of allPeaks) {
    let addedToCluster = false;
    for (const cluster of clusters) {
      if (cluster.some((p) => Math.abs(p - peak) < tolerance)) {
        cluster.push(peak);
        addedToCluster = true;
        break;
      }
    }
    if (!addedToCluster) {
      clusters.push([peak]);
    }
  }

  // Filter clusters with sufficient samples (sustained pitch)
  const significantClusters = clusters.filter((c) => c.length > 2);

  // Estimate speakers: 1-2 clusters = 1 speaker, 3-4 = 2 speakers, etc.
  if (significantClusters.length <= 2) return 1;
  if (significantClusters.length <= 4) return 2;
  if (significantClusters.length <= 6) return 3;
  return Math.min(5, Math.ceil(significantClusters.length / 2));
}

export function useAudioProctoring(config: AudioProctorConfig = {}) {
  const { rmsThreshold = 0.15, onFlag } = config;
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number>(0);
  
  // Debounce tracking: last time each flag was fired
  const lastFlagTimeRef = useRef<Record<string, number>>({});
  const lastWarningTimeRef = useRef<Record<AudioWarningSeverity, number>>({
    low: 0,
    medium: 0,
    high: 0,
  });

  const [audioStatus, setAudioStatus] = useState<AudioStatus>({
    currentVolume: 0,
    smoothedRms: 0,
    baselineRms: 0,
    calibrationProgress: 0,
    isCalibrated: false,
    voiceDetected: false,
    speakerCountEstimate: 0,
    flags: [],
    warningLevel: "none",
    warningMessage: null,
    timeSinceLastSpeech: 0,
    isListening: false,
    permissionGranted: false,
  });
  const audioStatusRef = useRef<AudioStatus>(audioStatus);

  const [currentRms, setCurrentRms] = useState<number>(0);
  const [isListening, setIsListening] = useState<boolean>(false);

  const calibrationStartRef = useRef<number | null>(null);
  const calibrationHistoryRef = useRef<number[]>([]);
  const lastSpeechTimeRef = useRef<number>(Date.now());
  const noiseBaselineRef = useRef<number[]>([]);
  const recentPeaksRef = useRef<number[][]>([]);
  const volumeHistoryRef = useRef<number[]>([]);
  const noiseSpikeAccRef = useRef<number>(0);
  const multiSpeakerAccRef = useRef<number>(0);
  const sustainedLowAccRef = useRef<number>(0);
  const sustainedMediumAccRef = useRef<number>(0);
  const sustainedHighAccRef = useRef<number>(0);
  const soundActiveRef = useRef<boolean>(false);

  // ── Helper: Fire flag with 3-second debouncing ────────────────────────────
  const fireFlag = useCallback(
    (
      type: AudioFlagPayload["type"],
      rms: number,
      severity?: AudioWarningSeverity,
      message?: string
    ) => {
      const now = Date.now();
      const lastTime = lastFlagTimeRef.current[type] || 0;

      if (now - lastTime < FLAG_DEBOUNCE_INTERVAL) {
        return;
      }

      lastFlagTimeRef.current[type] = now;

      if (onFlag) {
        onFlag({ type, rms, timestamp: now, severity, message });
      }
    },
    [onFlag]
  );
  // ── Start audio capture and analysis ─────────────────────────────────────
  const startListening = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyser.smoothingTimeConstant = SMOOTHING_TIME_CONSTANT;

      source.connect(analyser);
      analyserRef.current = analyser;

      calibrationStartRef.current = performance.now();
      calibrationHistoryRef.current = [];

      setAudioStatus((prev) => {
        const next: AudioStatus = {
          ...prev,
          isListening: true,
          permissionGranted: true,
          isCalibrated: false,
          calibrationProgress: 0,
          baselineRms: 0,
          warningLevel: "none",
          warningMessage: null,
        };
        audioStatusRef.current = next;
        return next;
      });
      setIsListening(true);

      // Start analysis loop
      analyzeAudio();
    } catch (err) {
      console.warn("Audio access denied:", err);
      setAudioStatus((prev) => ({
        ...prev,
        isListening: false,
        permissionGranted: false,
      }));
      setIsListening(false);
    }
  }, []);

  // ── Stop audio capture ────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    setAudioStatus((prev) => {
      const next: AudioStatus = {
        ...prev,
        isListening: false,
        currentVolume: 0,
        voiceDetected: false,
        speakerCountEstimate: 0,
      };
      audioStatusRef.current = next;
      return next;
    });
    setIsListening(false);
    setCurrentRms(0);
  }, []);

  // ── Audio analysis loop ───────────────────────────────────────────────────
  const analyzeAudio = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const frequencyArray = new Uint8Array(bufferLength);

    let lastAnalysisTime = performance.now();

    const analyze = () => {
      if (!analyserRef.current) return;

      const now = performance.now();
      const dt = now - lastAnalysisTime;

      // Throttle analysis to ANALYSIS_INTERVAL
      if (dt < ANALYSIS_INTERVAL) {
        animationFrameRef.current = requestAnimationFrame(analyze);
        return;
      }

      lastAnalysisTime = now;

      const sampleRate = audioContextRef.current?.sampleRate || 48000;
      const analysis = analyzeAudioFrame(
        analyser,
        dataArray,
        frequencyArray,
        sampleRate
      );

      const rms = analysis.volume / 100; // Convert 0-100 to 0-1
      setCurrentRms(rms);

      // Update volume smoothing
      volumeHistoryRef.current.push(rms);
      if (volumeHistoryRef.current.length > MOVING_AVERAGE_WINDOW) {
        volumeHistoryRef.current.shift();
      }
      const smoothedRms =
        volumeHistoryRef.current.reduce((sum, value) => sum + value, 0) /
        volumeHistoryRef.current.length;

      const nowMs = Date.now();
      const runtimeAudioStatus = audioStatusRef.current;
      const isQuiet = smoothedRms < rmsThreshold;
      const adjustedRms = Math.max(0, smoothedRms - (runtimeAudioStatus.baselineRms || 0));
      const speechRatio = analysis.totalEnergy > 0 ? analysis.voiceEnergy / analysis.totalEnergy : 0;
      const lowHumRatio = analysis.lowFrequencyEnergy / Math.max(1, analysis.totalEnergy);
      const isSpeechLike = speechRatio > 0.28 && analysis.voiceDetected && adjustedRms > 0.02;
      const likelySpeech = isSpeechLike && lowHumRatio < 0.35;

      // Calibration phase
      let baselineRms = runtimeAudioStatus.baselineRms;
      let calibrationProgress = runtimeAudioStatus.calibrationProgress;
      let isCalibrated = runtimeAudioStatus.isCalibrated;
      if (!isCalibrated && calibrationStartRef.current !== null) {
        calibrationHistoryRef.current.push(rms);
        if (calibrationHistoryRef.current.length > MOVING_AVERAGE_WINDOW) {
          calibrationHistoryRef.current.shift();
        }
        const elapsed = Math.min(
          CALIBRATION_DURATION_MS,
          nowMs - calibrationStartRef.current
        );
        calibrationProgress = elapsed / CALIBRATION_DURATION_MS;

        if (elapsed >= CALIBRATION_DURATION_MS) {
          baselineRms =
            calibrationHistoryRef.current.reduce((sum, value) => sum + value, 0) /
            calibrationHistoryRef.current.length;
          isCalibrated = true;
        }
      }

      // Update noise baseline history only after calibration completes
      if (isCalibrated) {
        noiseBaselineRef.current.push(analysis.noiseLevel);
        if (noiseBaselineRef.current.length > MOVING_AVERAGE_WINDOW) {
          noiseBaselineRef.current.shift();
        }
      }
      const avgNoise =
        noiseBaselineRef.current.length > 0
          ? noiseBaselineRef.current.reduce((a, b) => a + b, 0) /
            noiseBaselineRef.current.length
          : 0;

      // Maintain speech timing
      if (analysis.voiceDetected) {
        lastSpeechTimeRef.current = nowMs;
      }
      const timeSinceLastSpeech = nowMs - lastSpeechTimeRef.current;

      // Speaker estimation history
      if (analysis.frequencyPeaks.length > 0) {
        recentPeaksRef.current.push(analysis.frequencyPeaks);
        if (recentPeaksRef.current.length > 15) {
          recentPeaksRef.current.shift();
        }
      }
      const speakerCount = estimateSpeakerCount(
        recentPeaksRef.current,
        analysis.frequencyPeaks
      );

      const flags: string[] = [];
      let warningLevel: AudioWarningSeverity | "none" = "none";
      let warningMessage: string | null = null;
      const shouldWarn = isCalibrated && !isQuiet;

      // Sustained detection accumulators - increased thresholds for less sensitivity
      if (shouldWarn && likelySpeech) {
        sustainedHighAccRef.current += dt;
        sustainedMediumAccRef.current = Math.max(0, sustainedMediumAccRef.current - dt * 2);
        sustainedLowAccRef.current = Math.max(0, sustainedLowAccRef.current - dt * 2);
      } else if (shouldWarn && speechRatio > 0.25) { // Increased from 0.18
        sustainedMediumAccRef.current += dt;
        sustainedHighAccRef.current = Math.max(0, sustainedHighAccRef.current - dt * 2);
        sustainedLowAccRef.current = Math.max(0, sustainedLowAccRef.current - dt * 2);
      } else if (shouldWarn && adjustedRms > 0.05) { // Increased from 0.01
        sustainedLowAccRef.current += dt;
        sustainedMediumAccRef.current = Math.max(0, sustainedMediumAccRef.current - dt * 2);
        sustainedHighAccRef.current = Math.max(0, sustainedHighAccRef.current - dt * 2);
      } else {
        sustainedLowAccRef.current = Math.max(0, sustainedLowAccRef.current - dt * 3);
        sustainedMediumAccRef.current = Math.max(0, sustainedMediumAccRef.current - dt * 3);
        sustainedHighAccRef.current = Math.max(0, sustainedHighAccRef.current - dt * 3);
      }

      const cooldownPassed = (level: AudioWarningSeverity) =>
        nowMs - (lastWarningTimeRef.current[level] || 0) >= WARNING_COOLDOWN_MS;

      if (sustainedHighAccRef.current > SUSTAINED_SOUND_THRESHOLD_MS * 1.5 && cooldownPassed("high")) { // Increased threshold
        warningLevel = "high";
        warningMessage = "Background speech detected";
        flags.push("background_speech");
        lastWarningTimeRef.current.high = nowMs;
        fireFlag("background_speech", rms, "high", warningMessage);
      } else if (sustainedMediumAccRef.current > SUSTAINED_SOUND_THRESHOLD_MS * 1.2 && cooldownPassed("medium")) { // Increased threshold
        warningLevel = "medium";
        warningMessage = "Sustained environmental sound detected";
        flags.push("background_speech");
        lastWarningTimeRef.current.medium = nowMs;
        fireFlag("background_speech", rms, "medium", warningMessage);
      } else if (sustainedLowAccRef.current > SUSTAINED_SOUND_THRESHOLD_MS * 2 && cooldownPassed("low")) { // Much higher threshold for low-level noise
        warningLevel = "low";
        warningMessage = "Minor ambient noise detected";
        flags.push("background_noise");
        lastWarningTimeRef.current.low = nowMs;
        fireFlag("background_noise", rms, "low", warningMessage);
      }



      if (timeSinceLastSpeech > PROLONGED_SILENCE_THRESHOLD) {
        flags.push("prolonged_silence");
        fireFlag("prolonged_silence", rms, "low", "Extended silence detected - monitoring active");
      }

      const currentNoise = analysis.noiseLevel;
      if (avgNoise > 0 && currentNoise > avgNoise + NOISE_SPIKE_THRESHOLD) {
        noiseSpikeAccRef.current += dt;
      } else {
        noiseSpikeAccRef.current = Math.max(0, noiseSpikeAccRef.current - dt);
      }
      if (noiseSpikeAccRef.current > SHORT_SOUND_IGNORE_MS) {
        flags.push("noise_spike");
        fireFlag("noise_spike", rms, "medium", "Sudden noise spike detected");
      }

      setAudioStatus((prev) => {
        const next: AudioStatus = {
          ...prev,
          currentVolume: analysis.volume,
          smoothedRms,
          baselineRms,
          calibrationProgress,
          isCalibrated,
          voiceDetected: analysis.voiceDetected,
          speakerCountEstimate: speakerCount,
          flags,
          warningLevel,
          warningMessage,
          timeSinceLastSpeech,
        };
        audioStatusRef.current = next;
        return next;
      });

      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  }, [rmsThreshold, fireFlag]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return {
    startListening,
    stopListening,
    isListening,
    currentRms,
    audioStatus,
  };
}
